import {
  applyDiscountCode,
  ApplyDiscountCodeForm,
  ApplyDiscountCodeSchema,
  basketQueryKeys,
  CreateTransactionForm,
  CreateTransactionSchema,
  removeDiscountCode,
  useAuthGuest,
  useCheckoutBasketMutation,
  useCreateTransaction,
  useGetConsentWithMultipleProfesionnalsQuery,
  useGetEventFormQuestionsForMultipleEventsQuery,
  useGetHasCartWarning,
  useMyUserQuery,
  useSyncBasketMutation,
  useUpsertConsentMultipleProfesionnals,
} from "@ac-expo/api";
import { BuyableItemCreationType, CartOrder, MyUserType } from "@ac-expo/api-types";
import { clearGuestCartItems, useSyncedCart } from "@ac-expo/hooks";
import {
  Button,
  cn,
  Sheet,
  SheetClose,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  Stepper,
  useIsMobile,
  useToast,
} from "@ac-expo/ui-components";
import { CheckCircle, CircleOff, CircleX } from "lucide-react";
import React, { useCallback, useEffect, useMemo, useState } from "react";

import {
  centsToPrice,
  getCartRefusals,
  getErrorMessage,
  GROUP_TICKET_EMAIL_LOCK_MESSAGE,
  hasGroupTicketInCart,
  MINIMUM_CARD_PAYMENT_CENTS,
  priceToCents,
  shouldSyncLocalCartToServer,
} from "@ac-expo/utils";
import { zodResolver } from "@hookform/resolvers/zod";
import { Warning } from "@phosphor-icons/react";
import { OrderStatus } from "@prisma/client";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useConsent } from "@web-app/src/hooks/useConsent";
import { isAxiosError } from "axios";
import { uniq } from "lodash";
import Link from "next/link";
import { useRouter } from "next/router";
import { useForm } from "react-hook-form";
import AuthStore from "../../services/AuthStore";
import { useUserContext } from "../../context/auth/user.context";
import { SvgWeb } from "../../../public/images/svg/SvgWeb";
import { useCartContext } from "../../context/cart.context";
import { EventFormsProvider, useEventFormsContext } from "../../context/event-forms.context";
import { ArrowLeftIcon } from "../../icons";
import CheckoutCart from "./CheckoutCart";
import { EventFormsStep } from "./EventFormsStep";
import {
  buildPurchaseTunnelSteps,
  isPurchaseTunnelStepId,
  PurchaseTunnelStepId,
} from "./purchase-tunnel-steps";
import TicketsCart from "./TicketsCart";
import {
  guestIdentityFields,
  guestIdentitySchema,
  GuestIdentityField,
  navigateToPayment,
} from "./utils";

type TransactionFormValues = CreateTransactionForm & {
  email?: string;
  firstName?: string;
  lastName?: string;
};

type MinimumPaymentWarningProps = {
  warning: string;
};

function MinimumPaymentWarning({ warning }: MinimumPaymentWarningProps) {
  return (
    <div className="flex items-center gap-2 rounded-lg bg-orange-50 p-3">
      <Warning color="orange" size={20} className="shrink-0" />
      <p className="text-sm font-medium text-orange-700">{warning}</p>
    </div>
  );
}

type FooterCartOrderProps = {
  cartOrder: CartOrder;
  hasError: boolean;
  isMobile: boolean;
  onClose: () => void;
};

function FooterCartOrder({ cartOrder, hasError, isMobile, onClose }: FooterCartOrderProps) {
  const router = useRouter();
  const isSuccess = cartOrder.status === OrderStatus.success;
  const handleViewTickets = () => {
    onClose();
    router.push("/profile/mes-billets");
  };

  return (
    <SheetFooter className="mt-auto border-t p-4 pb-0">
      <div className="flex w-full flex-col items-center gap-2">
        <div className="flex w-full items-center justify-center gap-2">
          {isSuccess && (
            <button
              disabled={hasError}
              onClick={handleViewTickets}
              className={cn(
                "rounded-lg border border-blue-500 px-9 py-[10px] text-base font-semibold text-blue-500 disabled:bg-gray-300",
                { "px-4": isMobile },
              )}
            >
              Voir mes billets
            </button>
          )}
        </div>
        {isSuccess && !hasError && (
          <p className="text-center text-xs text-gray-600">
            Retrouvez vos factures dans la page{" "}
            <Link
              href="/profile/mes-billets"
              onClick={handleViewTickets}
              className="text-blue-500"
            >
              Mes billets
            </Link>
          </p>
        )}
      </div>
    </SheetFooter>
  );
}

type FooterCheckoutProps = {
  isBelowMinimum: boolean;
  minimumCardPaymentWarning: string;
  cartDiscount: number | undefined;
  cartDiscountedTotal: number;
  hasError: boolean;
  isAwaitingUserData: boolean;
  isMobile: boolean;
  isPaidCheckout: boolean;
  onCheckout: () => void;
};

function FooterCheckout({
  isBelowMinimum,
  minimumCardPaymentWarning,
  cartDiscount,
  cartDiscountedTotal,
  hasError,
  isAwaitingUserData,
  isMobile,
  isPaidCheckout,
  onCheckout,
}: FooterCheckoutProps) {
  const checkoutLabel = isPaidCheckout ? " avec obligation de paiement" : "";

  return (
    <SheetFooter className="mt-auto flex flex-col gap-2 border-t p-4 pb-0">
      {isBelowMinimum && <MinimumPaymentWarning warning={minimumCardPaymentWarning} />}
      <div className="flex items-center justify-between gap-2">
        <div className="flex flex-col gap-1">
          <p className="text-2xl font-bold">
            {(cartDiscount ?? 0) > 0 && (
              <span className="text-sm text-gray-500 line-through">{`${(cartDiscountedTotal + (cartDiscount ?? 0)).toFixed(2)}€`}</span>
            )}
            <span>{` ${cartDiscountedTotal.toFixed(2)}€`}</span>
          </p>
          <p className="text-sm font-medium text-gray-500">TOTAL</p>
        </div>
        <button
          disabled={hasError || isAwaitingUserData || isBelowMinimum}
          onClick={onCheckout}
          className={cn(
            "rounded-lg bg-blue-500 py-[10px] font-semibold text-white disabled:bg-gray-300 md:px-4",
            {
              "px-[10px] text-sm": isMobile,
              "text-base": !isMobile,
            },
          )}
        >
          {isAwaitingUserData ? (
            "Vérification..."
          ) : isMobile ? (
            <>
              Commander{" "}
              {isPaidCheckout ? (
                <>
                  avec obligation <br /> de paiement
                </>
              ) : null}
            </>
          ) : (
            `Commander${checkoutLabel}`
          )}
        </button>
      </div>
    </SheetFooter>
  );
}

type FooterCartProps = {
  isBelowMinimum: boolean;
  minimumCardPaymentWarning: string;
  discount: number;
  total: number;
  hasError: boolean;
  onValidate: () => void;
  activityWarning: React.ReactNode;
};

function FooterCart({
  isBelowMinimum,
  minimumCardPaymentWarning,
  discount,
  total,
  hasError,
  onValidate,
  activityWarning,
}: FooterCartProps) {
  return (
    <SheetFooter className="mt-auto flex flex-col gap-2 border-t p-4 pb-0">
      {isBelowMinimum && <MinimumPaymentWarning warning={minimumCardPaymentWarning} />}
      <div className="flex items-center justify-between">
        <div className="flex flex-col gap-1">
          <p className="text-2xl font-bold">
            {discount > 0 && (
              <span className="text-sm text-gray-500 line-through">{`${(total + discount).toFixed(2)}€`}</span>
            )}
            <span>{` ${total.toFixed(2)}€`}</span>
          </p>
          <p className="text-sm font-medium text-gray-500">TOTAL</p>
        </div>
        <button
          disabled={hasError || isBelowMinimum}
          onClick={onValidate}
          className="rounded-lg bg-blue-500 px-9 py-[10px] text-base font-semibold text-white disabled:bg-gray-300"
        >
          Valider mon panier
        </button>
      </div>
      {activityWarning}
    </SheetFooter>
  );
}

const getTransactionFormDefaultValues = (
  userData: MyUserType | undefined,
  total: number,
  consentData:
    | {
        structureId: string | null;
        organiserId: string | null;
        hasConsented: boolean | null;
      }[]
    | undefined,
  organiserIds: string[],
  structureIds: string[],
) => {
  const processConsents = (ids: string[], key: "organiserId" | "structureId") => {
    const uniqueIds = uniq(ids);

    return uniqueIds.reduce<{ id: string; hasConsented: boolean }[]>((acc, id) => {
      const hasConsented = consentData?.find((c) => c[key] === id)?.hasConsented;

      if (!hasConsented) {
        acc.push({ id, hasConsented: false });
      } else {
        acc = acc.filter((entry) => entry.id !== id);
      }

      return acc;
    }, []);
  };

  const organisers = processConsents(organiserIds, "organiserId");
  const structures = processConsents(structureIds, "structureId");

  const baseDefaultValues = {
    acceptedTerms: false,
    organisers,
    structures,
  };

  const guestIdentityValues = userData?.isGuestCheckout
    ? {
        email: userData.email ?? "",
        firstName: userData.firstName ?? "",
        lastName: userData.lastName ?? "",
      }
    : {};

  if (!userData || total === 0) {
    return { ...baseDefaultValues, ...guestIdentityValues };
  }

  return {
    address: {
      firstName: userData.firstName ?? "",
      lastName: userData.lastName ?? "",
      address1: userData.address ?? "",
      zipCode: userData.zipCode ?? "",
      city: userData.city ?? "",
      countryCode: "250",
    },
    ...baseDefaultValues,
    ...guestIdentityValues,
  };
};

function CartPanelContent() {
  const isMobile = useIsMobile();
  const queryClient = useQueryClient();
  const { isOpen, cartOrder, toggleCart, updateCartOrder } = useCartContext();
  const [isCheckout, setIsCheckout] = useState(false);
  const [isPaidCheckout, setIsPaidCheckout] = useState(false);
  const [isEventFormsStep, setIsEventFormsStep] = useState(false);
  const [isGuestChoiceStep, setIsGuestChoiceStep] = useState(false);
  const [hasAttemptedGuestIdentity, setHasAttemptedGuestIdentity] = useState(false);
  const [isApplyingDiscount, setIsApplyingDiscount] = useState(false);
  const { submitStoredForms, clearFormAnswers, cleanOrphanedFormAnswers } = useEventFormsContext();
  const { data: userData, isLoading: isUserLoading, refetch: refetchUser } = useMyUserQuery();
  const { user, isAuthenticated } = useUserContext();
  const { mutateAsync: authGuest } = useAuthGuest();
  const { mutateAsync: syncBasket } = useSyncBasketMutation("store");
  const { mutate: checkoutBasket } = useCheckoutBasketMutation();
  const { mutate: upsertConsent } = useUpsertConsentMultipleProfesionnals();
  const { mutateAsync: createTransaction } = useCreateTransaction();
  const { mutateAsync: applyDiscountCodeMutation } = useMutation({
    mutationFn: applyDiscountCode,
  });
  const { mutateAsync: removeDiscountCodeMutation } = useMutation({
    mutationFn: removeDiscountCode,
  });
  const { toast } = useToast();
  const cartReturns = useSyncedCart({ basketType: "store", isAuthenticated });
  const {
    cart,
    totalPricing,
    baseCartHasError: hasError,
    forceMutate,
    setOrderRedirect,
    hasItems,
    isSynced,
    cartItems,
    getErrors,
  } = cartReturns;

  // Reasons the server refused the basket, read back from the cart cache the
  // validation response invalidated.
  const refusals = useMemo(
    () => getCartRefusals(cartItems, getErrors),
    [cartItems, getErrors],
  );

  const total = totalPricing.total + (totalPricing.codeDiscount ?? 0);
  const discount = totalPricing.degressiveDiscount ?? 0;
  const router = useRouter();
  const cartDiscount = totalPricing.discount;
  const cartDiscountedTotal = totalPricing.total;
  const isAwaitingUserData = Boolean(user) && isUserLoading;

  const minimumCardPaymentPrice = centsToPrice(MINIMUM_CARD_PAYMENT_CENTS);
  const isBelowMinimumCardPaymentForCart = total > 0 && total < minimumCardPaymentPrice;
  const isBelowMinimumCardPaymentForCheckout =
    cartDiscountedTotal > 0 && cartDiscountedTotal < minimumCardPaymentPrice;
  const minimumCardPaymentWarning = "Le montant minimum pour un paiement par carte est de 1 €.";

  const { organiserIds, structureIds, organisersMapping, structuresMapping } = useConsent({
    cart,
  });

  const { data: consentData } = useGetConsentWithMultipleProfesionnalsQuery(
    organiserIds,
    structureIds,
  );

  const needsConsent =
    userData?.id !== undefined &&
    (consentData?.some((c) => !c.hasConsented) || !consentData || consentData.length === 0);

  const discountCodeFormDefaultValues = useMemo(
    () => ({
      basketId: cart?.id,
    }),
    [cart?.id],
  );
  const discountCodeForm = useForm<ApplyDiscountCodeForm>({
    resolver: zodResolver(ApplyDiscountCodeSchema),
    defaultValues: discountCodeFormDefaultValues,
  });
  const transactionFormDefaultValues = useMemo(
    () => getTransactionFormDefaultValues(userData, total, consentData, organiserIds, structureIds),
    [consentData, total, userData, organiserIds, structureIds],
  );
  const transactionForm = useForm<TransactionFormValues>({
    resolver: zodResolver(
      CreateTransactionSchema.extend({
        address: CreateTransactionSchema.shape.address.omit({
          businessName: true,
        }),
      }),
    ),
    defaultValues: transactionFormDefaultValues,
  });

  useEffect(() => {
    if (transactionForm.formState.isDirty) {
      return;
    }
    transactionForm.reset(transactionFormDefaultValues);
  }, [transactionFormDefaultValues]);

  useEffect(() => {
    if (cart?.id) {
      discountCodeForm.reset(discountCodeFormDefaultValues);
    }
  }, [cart?.id, discountCodeFormDefaultValues]);

  useEffect(() => {
    if (!isOpen) {
      setIsCheckout(false);
      setIsPaidCheckout(false);
      setIsEventFormsStep(false);
      setIsGuestChoiceStep(false);
    }
  }, [isOpen]);

  const { data: missingTickets } = useGetHasCartWarning();

  const eventIds = useMemo(() => {
    if (cartItems.length === 0) return [];

    const ids = new Set<string>();
    cartItems.forEach((item) => {
      const buyable = item.buyableItem;
      if (buyable?.ticketType?.event?.id) {
        ids.add(buyable.ticketType.event.id);
      }
      if (buyable?.badgeType?.event?.id) {
        ids.add(buyable.badgeType.event.id);
      }
    });

    return Array.from(ids);
  }, [cartItems]);

  const { data: eventFormsData } = useGetEventFormQuestionsForMultipleEventsQuery(eventIds);

  const ticketForms = useMemo(() => {
    if (!eventFormsData || cartItems.length === 0) return [];

    const getEventId = (item: (typeof cartItems)[number]) =>
      item.buyableItem?.ticketType?.event?.id || item.buyableItem?.badgeType?.event?.id;

    return cartItems
      .filter((item) => {
        const eventId = getEventId(item);
        if (!eventId) return false;
        if (item.buyableItem?.ticketType?.activityId) return false;

        const eventForm = eventFormsData[eventId];
        return eventForm && eventForm.questions.length > 0;
      })
      // Cart items are grouped per ticket type with a quantity: the form is
      // asked once per unit, each participant answering for their own ticket
      .flatMap((item) => {
        const buyable = item.buyableItem;
        const eventId = getEventId(item) as string;
        const eventForm = eventFormsData[eventId];
        const ticketName = buyable?.ticketType?.name || buyable?.badgeType?.name || "";

        return Array.from({ length: item.quantity }, (_, unitIndex) => ({
          ticketId: `${item.key}#${unitIndex}`,
          eventId,
          eventName: eventForm.eventName,
          ticketName,
          organiserId: eventForm.organiserId,
          eventType: eventForm.eventType,
          questions: eventForm.questions,
        }));
      })
      .map((ticketForm, index) => ({ ...ticketForm, ticketIndex: index + 1 }));
  }, [eventFormsData, cartItems]);

  const eventFormsCount = ticketForms.length;

  useEffect(() => {
    const currentTicketIds = new Set(ticketForms.map((form) => form.ticketId));
    cleanOrphanedFormAnswers(currentTicketIds);
  }, [ticketForms, cleanOrphanedFormAnswers]);

  // Leaving the payment step forgets the failed attempt, including the promo
  // hint that pointed at the empty identity fields
  const resetGuestIdentityAttempt = () => {
    setHasAttemptedGuestIdentity(false);
    discountCodeForm.clearErrors("discountCode");
  };

  const onCloseSuccess = () => {
    setIsCheckout(false);
    setIsEventFormsStep(false);
    resetGuestIdentityAttempt();
    updateCartOrder(null);
  };

  function handleClose() {
    if (cartOrder?.status === OrderStatus.success) {
      onCloseSuccess();
    }

    toggleCart(false);
    forceMutate();
  }

  const goToNextStepAfterAuth = useCallback(() => {
    if (!hasError) {
      if (eventFormsCount > 0) {
        setIsEventFormsStep(true);
      } else {
        setIsPaidCheckout(total > 0);
        setIsCheckout(true);
      }
    }
  }, [hasError, eventFormsCount, total]);

  useEffect(() => {
    if (user && isGuestChoiceStep) {
      setIsGuestChoiceStep(false);
    }
  }, [user, isGuestChoiceStep]);

  function handleValidate() {
    const goToNextStep = () => {
      if (!user) {
        setIsGuestChoiceStep(true);
        return;
      }
      goToNextStepAfterAuth();
    };

    if (isSynced) {
      goToNextStep();
      return;
    }

    forceMutate({
      onSuccess() {
        goToNextStep();
      },
    });
  }

  const handleOpenLogin = () => {
    AuthStore.setModalVisible(true);
  };

  const handleContinueAsGuest = () => {
    setIsGuestChoiceStep(false);
    goToNextStepAfterAuth();
  };

  const handleBackFromGuestChoice = () => {
    setIsGuestChoiceStep(false);
  };

  // A Group ticket was unlocked for the guest email that opened the session (ADR 0005):
  // recreating the guest under another email would detach the order from that unlock.
  const refuseEmailChangeWithGroupTickets = (isExistingGuestWithEmailChange: boolean) => {
    if (!isExistingGuestWithEmailChange || !hasGroupTicketInCart(cartItems)) {
      return false;
    }
    toast({ title: "Erreur", description: GROUP_TICKET_EMAIL_LOCK_MESSAGE });
    return true;
  };

  // A discount code is persisted on the server-side basket, so a guest must be
  // authenticated and synced before one can be applied. We create the guest
  // account on first apply (identity required) and reuse it afterwards.
  const prepareGuestBasketForDiscount = async (): Promise<string | null> => {
    const guestValues = {
      email: transactionForm.getValues("email") ?? "",
      firstName: transactionForm.getValues("firstName") ?? "",
      lastName: transactionForm.getValues("lastName") ?? "",
    };
    const isExistingGuestWithEmailChange =
      Boolean(userData?.isGuestCheckout) && userData?.email !== guestValues.email;
    const shouldCreateGuestUser = !user || isExistingGuestWithEmailChange;

    if (cartItems.length === 0) {
      return null;
    }

    if (refuseEmailChangeWithGroupTickets(isExistingGuestWithEmailChange)) {
      return null;
    }

    if (!shouldCreateGuestUser) {
      // A returning guest keeps its items in localStorage (the guest session is
      // not recognised client-side), so the server basket may still be empty:
      // sync it before applying the code, as handleCheckout does.
      const mustSyncLocalCart = shouldSyncLocalCartToServer({
        isGuestCheckoutUser: Boolean(userData?.isGuestCheckout),
        isAuthenticated,
        hasServerBasket: Boolean(cart?.id),
        localItemsCount: cartItems.length,
      });
      if (!mustSyncLocalCart) {
        return cart?.id ?? null;
      }
      try {
        const synced = await syncCartItemsToBasket();
        return synced.id;
      } catch (error) {
        toast({
          title: "Erreur",
          description: getErrorMessage(error, "Erreur lors de la synchronisation du panier."),
        });
        return null;
      }
    }

    setHasAttemptedGuestIdentity(true);
    const result = guestIdentitySchema.safeParse(guestValues);
    if (!result.success) {
      const issuesByField = new Map<GuestIdentityField, string>();
      result.error.issues.forEach((issue) => {
        const field = issue.path[0];
        if (field === "email" || field === "firstName" || field === "lastName") {
          issuesByField.set(field, issue.message);
        }
      });
      guestIdentityFields.forEach((field) => {
        const message = issuesByField.get(field);
        if (message) {
          transactionForm.setError(field, { message });
        }
      });
      // The identity fields sit below the promo input, so a silent failure here
      // reads as "nothing happens". Surface the reason where the user clicked.
      discountCodeForm.setError("discountCode", {
        message:
          "Renseignez votre email, votre nom et votre prénom dans la section Identité pour appliquer un code promo.",
      });
      return null;
    }

    try {
      await authGuest(guestValues);
      await refetchUser();
      const synced = await syncCartItemsToBasket();
      return synced.id;
    } catch (error) {
      toast({
        title: "Erreur",
        description: getErrorMessage(
          error,
          "Une erreur est survenue lors de la création du compte invité.",
        ),
      });
      return null;
    }
  };

  const handleApplyDiscountCode = async () => {
    const isValid = await discountCodeForm.trigger("discountCode");
    if (!isValid) {
      return;
    }
    const discountCode = discountCodeForm.getValues("discountCode");

    setIsApplyingDiscount(true);
    try {
      const basketId = await prepareGuestBasketForDiscount();
      if (!basketId) {
        return;
      }

      const results = await applyDiscountCodeMutation({ basketId, discountCode });
      if (results.length > 0) {
        toast({
          description: results.map((result) => result.replacedCodesMessage).join("\n"),
          duration: 5000,
        });
      }
      queryClient.invalidateQueries({ queryKey: basketQueryKeys.get("store") });
      discountCodeForm.setValue("discountCode", "");
      discountCodeForm.clearErrors("discountCode");
    } catch (error) {
      discountCodeForm.setError("discountCode", {
        message: getErrorMessage(
          error,
          "Une erreur est survenue, le code n'a pas pu être appliqué",
        ),
      });
    } finally {
      setIsApplyingDiscount(false);
    }
  };

  const handleRemoveDiscountCode = async (discountCodeValue: string) => {
    if (!cart) {
      return;
    }
    const codes = cart.appliedDiscountCodes.filter((code) => code.code === discountCodeValue);
    try {
      await Promise.all(
        codes.map((code) =>
          removeDiscountCodeMutation({ discountCodeId: code.id, basketId: cart.id }),
        ),
      );
      queryClient.invalidateQueries({ queryKey: basketQueryKeys.get("store") });
      discountCodeForm.clearErrors("discountCode");
    } catch (error) {
      discountCodeForm.setError("discountCode", {
        message: getErrorMessage(
          error,
          "Une erreur est survenue, le code n'a pas pu être retiré",
        ),
      });
    }
  };

  const syncCartItemsToBasket = async () => {
    const syncResult = await syncBasket({
      type: "store",
      items: cartItems.map((item) => ({
        buyableItem: item.buyableItem as BuyableItemCreationType,
        quantity: item.quantity,
      })),
    });
    clearGuestCartItems("store");
    return { id: syncResult.basket.id, finalPrice: syncResult.basket.finalPrice };
  };

  const validateGuestIdentityFields = (guestValues: {
    email: string;
    firstName: string;
    lastName: string;
  }): boolean => {
    const result = guestIdentitySchema.safeParse(guestValues);
    const issuesByField = new Map<GuestIdentityField, string>();

    if (!result.success) {
      result.error.issues.forEach((issue) => {
        const field = issue.path[0];
        if (field === "email" || field === "firstName" || field === "lastName") {
          issuesByField.set(field, issue.message);
        }
      });
    }

    guestIdentityFields.forEach((field) => {
      const message = issuesByField.get(field);
      if (!message) {
        if (transactionForm.getFieldState(field).error) {
          transactionForm.clearErrors(field);
        }
        return;
      }
      transactionForm.setError(field, { message });
    });

    return result.success;
  };

  const handleCheckoutError = (error: unknown) => {
    if (!isAxiosError(error)) {
      toast({ title: "Erreur", description: "Une erreur est survenue." });
      queryClient.invalidateQueries({ queryKey: basketQueryKeys.get("store") });
      return;
    }

    const data = error.response?.data;

    // Per-line refusals are shown on the checkout screen, attached to their ticket,
    // once the invalidation below refreshes the basket: no generic toast for them.
    if (data?.discountError) {
      const { previousPrice, newPrice } = data.discountError;
      const priceDiff = (previousPrice - newPrice) / 100;
      toast({
        title: "Erreur",
        description:
          previousPrice !== newPrice
            ? `Le prix de votre panier a changé (différence de ${priceDiff.toFixed(2)}€). Veuillez vérifier votre commande.`
            : "Un ou plusieurs codes promos ont expiré ou ne sont plus disponibles.",
      });
    }

    queryClient.invalidateQueries({ queryKey: basketQueryKeys.get("store") });
  };

  const handleTransactionSuccess = async (
    orderId: string,
    isGuestCheckout: boolean,
  ) => {
    const address = transactionForm.getValues().address;
    const payload = {
      orderId,
      address: (address?.address1.length ?? 0) > 0 ? address : undefined,
      ...(isGuestCheckout
        ? {
            email: transactionForm.getValues("email"),
            firstName: transactionForm.getValues("firstName"),
            lastName: transactionForm.getValues("lastName"),
          }
        : {}),
    };

    const result = await createTransaction(payload);

    try {
      await submitStoredForms(eventIds);
    } catch (error) {
      console.error("Error submitting event forms:", error);
    }

    if (!result.shouldPay) {
      queryClient.invalidateQueries({ queryKey: basketQueryKeys.get("store") });

      if (isGuestCheckout) {
        clearGuestCartItems("store");
        toggleCart(false);
        clearFormAnswers();
        setOrderRedirect(router.asPath);
        router.push(`/guest/after-purchase?orderId=${orderId}`);
        return;
      }

      setIsCheckout(false);
      updateCartOrder(result.data);
      clearFormAnswers();
      toggleCart(true);
      return;
    }

    setOrderRedirect(router.asPath);
    navigateToPayment(result.data);
  };

  const handleCheckout = async () => {
    if (isAwaitingUserData) return;

    const guestValues = {
      email: transactionForm.getValues("email") ?? "",
      firstName: transactionForm.getValues("firstName") ?? "",
      lastName: transactionForm.getValues("lastName") ?? "",
    };

    const needsGuestUserCreation = !user;
    const isExistingGuestWithEmailChange =
      Boolean(userData?.isGuestCheckout) && userData?.email !== guestValues.email;
    const shouldCreateGuestUser = needsGuestUserCreation || isExistingGuestWithEmailChange;

    if (refuseEmailChangeWithGroupTickets(isExistingGuestWithEmailChange)) {
      return;
    }

    // Validate all form fields BEFORE any side effect (authGuest, syncBasket, clearGuestCartItems).
    // Otherwise a failed validation leaves the user authenticated with a cleared local cart,
    // showing 0€ until the server basket query resolves.
    if (shouldCreateGuestUser) {
      setHasAttemptedGuestIdentity(true);
      if (!validateGuestIdentityFields(guestValues)) {
        return;
      }
    }

    const isAddressValid = isPaidCheckout ? await transactionForm.trigger("address") : true;
    const hasAcceptedTerms = await transactionForm.trigger("acceptedTerms");

    if (!isAddressValid || !hasAcceptedTerms) return;

    let checkoutBasketData: { id: string; finalPrice: number } | null = null;

    if (shouldCreateGuestUser) {
      try {
        await authGuest(guestValues);
        if (cartItems.length > 0) {
          checkoutBasketData = await syncCartItemsToBasket();
        }
      } catch (error) {
        toast({
          title: "Erreur",
          description: getErrorMessage(
            error,
            "Une erreur est survenue lors de la création du compte invité.",
          ),
        });
        return;
      }
    }

    const isGuestCheckout = shouldCreateGuestUser || Boolean(userData?.isGuestCheckout);

    // For guest checkout with existing user but no synced basket, sync now.
    // When !isAuthenticated, items are in localStorage (not on server) even if cart?.id exists.
    const mustSyncLocalCart = shouldSyncLocalCartToServer({
      isGuestCheckoutUser: isGuestCheckout,
      isAuthenticated,
      hasServerBasket: Boolean(cart?.id),
      localItemsCount: cartItems.length,
    });
    if (!checkoutBasketData && mustSyncLocalCart) {
      try {
        checkoutBasketData = await syncCartItemsToBasket();
      } catch (error) {
        toast({
          title: "Erreur",
          description: getErrorMessage(error, "Erreur lors de la synchronisation du panier."),
        });
        return;
      }
    }

    const basketToUse = checkoutBasketData ?? cart;
    if (!basketToUse) return;

    const runCheckout = () =>
      checkoutBasket(
        {
          basketId: basketToUse.id,
          displayedPrice: checkoutBasketData?.finalPrice ?? priceToCents(totalPricing.total),
        },
        {
          onSuccess: async ({ id }) => handleTransactionSuccess(id, isGuestCheckout),
          onError: handleCheckoutError,
        },
      );

    if (userData?.id && needsConsent) {
      upsertConsent(
        {
          organisers: transactionForm.getValues().organisers,
          structures: transactionForm.getValues().structures,
        },
        {
          onSuccess: () => runCheckout(),
          onError: () => {
            toast({ title: "Erreur", description: "Une erreur est survenue." });
          },
        },
      );
    } else {
      runCheckout();
    }
  };

  const handleEventFormsBack = () => {
    setIsEventFormsStep(false);
  };

  const handleEventFormsComplete = () => {
    setIsEventFormsStep(false);
    setIsPaidCheckout(total > 0);
    setIsCheckout(true);
  };

  // Straight back to the cart lines, skipping the event forms step the back arrow
  // walks through: the visitor has an item to correct there.
  const handleBackToCart = () => {
    setIsEventFormsStep(false);
    setIsCheckout(false);
    setIsPaidCheckout(false);
  };

  const handleBackFromCheckout = () => {
    if (eventFormsCount > 0) {
      setIsEventFormsStep(true);
    }
    setIsCheckout(false);
    setIsPaidCheckout(false);
    resetGuestIdentityAttempt();
  };

  const tunnelSteps = buildPurchaseTunnelSteps({
    isLoggedIn: Boolean(user),
    eventFormsCount,
    isPaid: total > 0,
  });

  const currentTunnelStepId: PurchaseTunnelStepId = isGuestChoiceStep
    ? "identification"
    : isEventFormsStep
      ? "forms"
      : isCheckout
        ? "payment"
        : "cart";

  const handleTunnelStepClick = (stepId: string) => {
    if (!isPurchaseTunnelStepId(stepId)) return;

    setIsCheckout(false);
    setIsPaidCheckout(false);
    resetGuestIdentityAttempt();
    setIsGuestChoiceStep(stepId === "identification");
    setIsEventFormsStep(stepId === "forms");
  };

  function renderTitle() {
    if (cartOrder) {
      return "Statut";
    }

    if (isGuestChoiceStep) {
      return (
        <div className="flex items-center gap-2">
          <button onClick={handleBackFromGuestChoice}>
            <ArrowLeftIcon width={20} height={20} />
          </button>
          <p>Finaliser ma commande</p>
        </div>
      );
    }

    if (isEventFormsStep) {
      return `Formulaire${eventFormsCount > 1 ? "s" : ""}`;
    }

    if (!isCheckout) {
      return "Mon panier";
    }

    return (
      <div className="flex items-center gap-2">
        <button onClick={handleBackFromCheckout}>
          <ArrowLeftIcon width={20} height={20} />
        </button>
        <p>{isPaidCheckout ? "Paiement" : "Confirmation"}</p>
      </div>
    );
  }

  function renderFooter() {
    if (isEventFormsStep || isGuestChoiceStep) return null;

    if (cartOrder) {
      return (
        <FooterCartOrder
          cartOrder={cartOrder}
          hasError={hasError}
          isMobile={isMobile}
          onClose={handleClose}
        />
      );
    }

    if (isCheckout) {
      return (
        <FooterCheckout
          isBelowMinimum={isBelowMinimumCardPaymentForCheckout}
          minimumCardPaymentWarning={minimumCardPaymentWarning}
          cartDiscount={cartDiscount}
          cartDiscountedTotal={cartDiscountedTotal}
          hasError={hasError}
          isAwaitingUserData={isAwaitingUserData}
          isMobile={isMobile}
          isPaidCheckout={isPaidCheckout}
          onCheckout={handleCheckout}
        />
      );
    }

    if (!hasItems) return null;

    return (
      <FooterCart
        isBelowMinimum={isBelowMinimumCardPaymentForCart}
        minimumCardPaymentWarning={minimumCardPaymentWarning}
        discount={discount}
        total={total}
        hasError={hasError}
        onValidate={handleValidate}
        activityWarning={renderActivityWarning()}
      />
    );
  }

  const renderActivityWarning = () => {
    if (!missingTickets) return null;

    const missingEventTickets =
      missingTickets.activityTicketQuantity - missingTickets.eventTicketQuantity;
    const shouldShowWarning = missingTickets.hasMultipleEvents || missingEventTickets > 0;

    if (!shouldShowWarning) return null;

    const warningMessage = missingTickets.hasMultipleEvents
      ? "ATTENTION, vous avez ajouté des billets animations dans votre panier. Vous devez également ajouter les billets d'entrées correspondants pour pouvoir accéder aux événements."
      : `ATTENTION, vous avez ajouté ${missingTickets.activityTicketQuantity} ${missingTickets.activityTicketQuantity > 1 ? "billets" : "billet"} d'animation dans votre panier. Vous devez également ajouter ${missingEventTickets} billet${missingEventTickets > 1 ? "s" : ""} d'entrée pour pouvoir accéder à l'événement`;

    return (
      <div className="flex items-center gap-2">
        <Warning color="orange" size={24} />
        <p className="text-xs italic text-gray-500">{warningMessage}</p>
      </div>
    );
  };

  return (
    <Sheet open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <SheetContent
        className={cn("flex w-full flex-col bg-gray-50 md:max-w-lg", {
          "px-0": isMobile,
        })}
      >
        <SheetHeader className="flex items-center justify-between border-b p-4 pt-0">
          <SheetTitle>{renderTitle()}</SheetTitle>
          <SheetClose asChild>
            <button className="focus:outline-none">
              <SvgWeb name="close" alt="close" width={24} height={24} />
            </button>
          </SheetClose>
        </SheetHeader>

        {!cartOrder && currentTunnelStepId !== "cart" && (
          <Stepper
            steps={tunnelSteps}
            currentStepId={currentTunnelStepId}
            onStepClick={handleTunnelStepClick}
            ariaLabel="Étapes de la commande"
            className="border-b bg-white px-6 py-3"
          />
        )}

        {cartOrder ? (
          <CartStatus order={cartOrder} />
        ) : isGuestChoiceStep ? (
          <div className="flex flex-1 flex-col items-center justify-center gap-6 p-6">
            <div className="flex flex-col items-center gap-2 text-center">
              <h3 className="text-xl font-semibold">Pour finaliser votre commande</h3>
              <p className="text-sm text-gray-500">
                Connectez-vous ou continuez en tant qu&apos;invité
              </p>
            </div>
            <div className="flex w-full max-w-sm flex-col gap-4">
              <Button variant="blue" onClick={handleOpenLogin} className="w-full">
                Se connecter / S&apos;inscrire
              </Button>
              <div className="flex items-center gap-2">
                <div className="h-px flex-1 bg-gray-300" />
                <span className="text-sm text-gray-500">ou</span>
                <div className="h-px flex-1 bg-gray-300" />
              </div>
              <Button variant="outline" onClick={handleContinueAsGuest} className="w-full">
                Continuer en tant qu&apos;invité
              </Button>
            </div>
          </div>
        ) : isEventFormsStep ? (
          <EventFormsStep
            ticketForms={ticketForms}
            onBack={handleEventFormsBack}
            onComplete={handleEventFormsComplete}
          />
        ) : !isCheckout ? (
          <TicketsCart {...cartReturns} />
        ) : (
          <CheckoutCart
            transactionForm={transactionForm}
            discountCodeForm={discountCodeForm}
            cart={cart}
            cartItems={cartItems}
            organisersMapping={organisersMapping}
            structuresMapping={structuresMapping}
            isPaid={isPaidCheckout}
            refusals={refusals}
            onBackToCart={handleBackToCart}
            onApplyDiscountCode={handleApplyDiscountCode}
            onRemoveDiscountCode={handleRemoveDiscountCode}
            isApplyingDiscount={isApplyingDiscount}
            showGuestIdentityErrors={hasAttemptedGuestIdentity}
          />
        )}

        {renderFooter()}
      </SheetContent>
    </Sheet>
  );
}

type CartStatusProps = {
  order: CartOrder;
  children?: React.ReactNode;
  disableDescription?: boolean;
};

export function CartStatus({ order, children, disableDescription }: CartStatusProps) {
  function renderContent() {
    if (order.status === OrderStatus.refused) {
      return (
        <>
          <CircleX size={40} color="red" />
          <h3 className="text-center font-semibold">Échec du paiement.</h3>
          {!disableDescription && (
            <p className="text-center text-sm">
              Une erreur est survenue et votre commande n&apos;a pas pu être finalisée.
            </p>
          )}
        </>
      );
    }

    if (order.status === OrderStatus.canceled) {
      return (
        <>
          <CircleOff size={40} color="orange" />
          <h3 className="text-center font-semibold">Abandon de la commande</h3>
          {!disableDescription && (
            <p className="text-center text-sm">
              La commande a été abandonnée. Vos articles se trouvent toujours dans votre panier.
            </p>
          )}
        </>
      );
    }

    if (order.status === OrderStatus.error) {
      return (
        <>
          <CircleOff size={40} color="orange" />
          <h3 className="text-center font-semibold">Erreur lors de la commande</h3>
          <p className="text-center text-sm">
            Il y a eu une erreur durant votre achat. Merci de nous contacter. Vos articles se
            trouvent toujours dans votre panier.
          </p>
        </>
      );
    }

    return (
      <>
        <CheckCircle size={40} color="green" />
        <h3 className="text-center font-semibold">Paiement effectué !</h3>
        {!disableDescription && (
          <p className="text-center text-sm">
            Retrouvez vos achats dans les onglets correspondants.
          </p>
        )}
        <p className="text-center text-xs">Référence : {order.reference}</p>
      </>
    );
  }

  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-4 overflow-y-auto">
      <div className="flex w-2/3 flex-col items-center justify-center gap-2 pb-8">
        {renderContent()}
        {children}
      </div>
    </div>
  );
}

function CartPanel() {
  return (
    <EventFormsProvider>
      <CartPanelContent />
    </EventFormsProvider>
  );
}

export default CartPanel;
